import type { StationDetailOut } from '@/api/stations';

export type StationWsEvent =
  | { event: 'snapshot'; station: StationDetailOut }
  | { event: 'station_changed'; station: StationDetailOut }
  | { event: 'ping' };

type Listener = (event: StationWsEvent) => void;

export class StationStatusSocket {
  private ws: WebSocket | null = null;
  private retries = 0;
  private closedByClient = false;
  private readonly listeners = new Set<Listener>();

  constructor(private readonly stationId: string) {}

  connect(): void {
    this.closedByClient = false;
    const base = this.resolveBase();
    const url = `${base}/ws/stations/${this.stationId}`;
    this.ws = new WebSocket(url);
    this.ws.onmessage = (msg) => this.dispatch(msg.data);
    this.ws.onopen = () => {
      this.retries = 0;
    };
    this.ws.onclose = () => {
      if (this.closedByClient) return;
      const delay = Math.min(30_000, 500 * 2 ** this.retries);
      this.retries += 1;
      window.setTimeout(() => this.connect(), delay);
    };
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  close(): void {
    this.closedByClient = true;
    this.ws?.close();
    this.listeners.clear();
  }

  private dispatch(raw: unknown) {
    if (typeof raw !== 'string') return;
    try {
      const event = JSON.parse(raw) as StationWsEvent;
      this.listeners.forEach((fn) => fn(event));
    } catch {
      // frame inválido — ignora
    }
  }

  private resolveBase(): string {
    const override = import.meta.env.VITE_WS_BASE;
    if (override) return override;
    const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
    return `${scheme}://${window.location.host}/api/v1`;
  }
}
